import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import dayjs from "dayjs";

import "./NotificationPage.css";
import { handleAuth } from "../Login/Auth";
import AxiosInstance from "../../utils/axiosInstance";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  DateRange as DateRangeIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { LoaderComponent } from "../Icon/Index";
import NotificationContainer from "./NotificationContainer";
import DatePicker from "../DatePicker/DatePicker";
import SearchInput from "../InputFields/SearchInput";

const DEBOUNCE_MS = 500;

const DebouncedSearchField = React.memo(({ value, onChange, placeholder, isDonationMode }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const timeoutRef = useRef(null);
  // Track last value we sent to parent so we don't overwrite user typing when parent re-renders
  const lastSentValueRef = useRef(value ?? "");

  // Only sync from prop when parent explicitly clears (e.g. Clear filters). Never sync non-empty value
  // so that parent re-renders after fetch never overwrite the input and break typing.
  useEffect(() => {
    if (value === "") {
      setInputValue("");
      lastSentValueRef.current = "";
    }
  }, [value]);

  useEffect(() => {
    if (inputValue === value) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      lastSentValueRef.current = inputValue;
      onChange(inputValue);
      timeoutRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [inputValue, value, onChange]);

  const handleClear = () => {
    setInputValue("");
    lastSentValueRef.current = "";
    onChange("");
  };

  return (
    <SearchInput
      fullWidth
      placeholder={placeholder}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      ariaLabel={placeholder || "Search notifications"}
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon
            sx={{
              color: isDonationMode ? "#68b2cb" : "#0f2b46",
              fontSize: "20px",
            }}
          />
        </InputAdornment>
      }
      endAdornment={
        inputValue ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              sx={{
                color: isDonationMode ? "#68b2cb" : "#0f2b46",
                "&:hover": {
                  backgroundColor: isDonationMode
                    ? "rgba(104, 178, 203, 0.1)"
                    : "rgba(6, 49, 100, 0.1)",
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null
      }
      fieldHeight="44px"
      style={{ marginBottom: 16 }}
    />
  );
});

DebouncedSearchField.displayName = "DebouncedSearchField";

const NotificationsPage = ({
  handleCloseNotifications,
  fetchNotifications,
  notifications,
  isDonationMode = false,
  loader = false,
  totalCount = 0,
  totalAllNotifications = 0,
  totalPages = 1,
  currentPage: backendCurrentPage = 1,
}) => {
  const { CompanyUrl } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [tokenDecode, setTokenDecode] = useState({});

  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [DateDecode, setDateDecode] = useState({});
  const [dateFilterOption, setDateFilterOption] = useState("last7days");
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  
  // State to control DatePicker open/close for scroll handling
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  // Close DatePickers when scrolling occurs in notification panel
  useEffect(() => {
    const handleScroll = () => {
      if (startDatePickerOpen) {
        setStartDatePickerOpen(false);
      }
      if (endDatePickerOpen) {
        setEndDatePickerOpen(false);
      }
    };

    // Find the scroll container (notification panel's scrollable area)
    const scrollContainer = document.querySelector('.notification-bar');
    if (scrollContainer && (startDatePickerOpen || endDatePickerOpen)) {
      // Add listener to the scrollable container inside notification bar
      const scrollableArea = scrollContainer.querySelector('[style*="overflow"]') || scrollContainer;
      scrollableArea.addEventListener('scroll', handleScroll, true);
      
      // Also listen on window scroll
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        scrollableArea.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [startDatePickerOpen, endDatePickerOpen]);

  const getDatePickerValue = (dateString) => {
    if (!dateString) return null;
    try {
      const parsed = dayjs(dateString);
      return parsed.isValid() ? parsed : null;
    } catch (error) {
      return null;
    }
  };

  const formatDateForAPI = (eventOrValue) => {
    try {
      let dateValue = null;
      if (eventOrValue && eventOrValue.target && eventOrValue.target.value !== undefined) {
        dateValue = eventOrValue.target.value;
      } else if (eventOrValue) {
        dateValue = eventOrValue;
      }
      
      if (!dateValue || dateValue === null) return "";
      
      if (dayjs.isDayjs(dateValue)) {
        return dateValue.format("YYYY-MM-DD");
      }
      
      if (typeof dateValue === "string") {
        if (dateValue.includes("T")) {
          return dateValue.split("T")[0];
        }
        const parsed = dayjs(dateValue);
        if (parsed.isValid()) {
          return parsed.format("YYYY-MM-DD");
        }
        const parsedWithFormat = dayjs(dateValue, dateFormat);
        if (parsedWithFormat.isValid()) {
          return parsedWithFormat.format("YYYY-MM-DD");
        }
      }
      
      return "";
    } catch (error) {
      console.error("Error formatting date for API:", error);
      return "";
    }
  };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(backendCurrentPage);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Sync with backend current page - only update if it actually changed
  useEffect(() => {
    if (backendCurrentPage !== currentPage) {
      setCurrentPage(backendCurrentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendCurrentPage]);

  const fetchData = async () => {
    try {
      const res = await handleAuth(navigate, location);
      setTokenDecode(res?.data);
      if (res?.themes?.Format) {
        setDateDecode(res?.themes);
      } else {
        setDateDecode({ Format: "MM/DD/YYYY" });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setDateDecode({ Format: "MM/DD/YYYY" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleDateFormat = () => {
      if (DateDecode?.Format) {
        try {
          const testDate = moment().format(DateDecode?.Format);
          if (testDate && testDate !== "Invalid date") {
            setDateFormat(DateDecode?.Format);
          } else {
            console.warn(
              "Invalid date format from backend, using default:",
              DateDecode?.Format
            );
            setDateFormat("MM/DD/YYYY");
          }
        } catch (error) {
          console.warn("Error validating date format, using default:", error);
          setDateFormat("MM/DD/YYYY");
        }
      } else {
        setDateFormat("MM/DD/YYYY");
      }
    };
    handleDateFormat();
  }, [DateDecode]);


  // Fetch notifications with parameters
  const fetchNotificationsWithParams = (page, limit, search, start, end) => {
    if (fetchNotifications && typeof fetchNotifications === 'function') {
      fetchNotifications(page, limit, search, start, end);
    }
  };

  // Use notifications directly from backend (already paginated and filtered)
  const paginatedNotifications = notifications || [];

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchNotificationsWithParams(value, itemsPerPage, searchQuery, startDate, endDate);
    // Scroll to top of notifications container
    const container = document.querySelector('.notificationsContainer');
    if (container) {
      container.scrollTop = 0;
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (event) => {
    const newLimit = event.target.value;
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    fetchNotificationsWithParams(1, newLimit, searchQuery, startDate, endDate);
  };

  // Fetch when search query changes (search input is already debounced in DebouncedSearchField)
  useEffect(() => {
    setCurrentPage(1);
    fetchNotificationsWithParams(1, itemsPerPage, searchQuery, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Handle date filter option change
  const handleDateFilterOptionChange = (event) => {
    const option = event.target.value;
    setDateFilterOption(option);

    if (option === "custom") {
      // Show date pickers for custom selection
      setStartDate("");
      setEndDate("");
      return;
    }

    // Calculate dates based on selected option
    const today = dayjs();
    let newStartDate = "";
    let newEndDate = "";

    switch (option) {
      case "last7days":
        newStartDate = today.subtract(7, "day").format("YYYY-MM-DD");
        newEndDate = today.format("YYYY-MM-DD");
        break;
      case "last30days":
        newStartDate = today.subtract(30, "day").format("YYYY-MM-DD");
        newEndDate = today.format("YYYY-MM-DD");
        break;
      case "thisMonth":
        newStartDate = today.startOf("month").format("YYYY-MM-DD");
        newEndDate = today.format("YYYY-MM-DD");
        break;
      case "lastMonth":
        newStartDate = today.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
        newEndDate = today.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
        break;
      case "thisYear":
        newStartDate = today.startOf("year").format("YYYY-MM-DD");
        newEndDate = today.format("YYYY-MM-DD");
        break;
      case "lastYear":
        newStartDate = today.subtract(1, "year").startOf("year").format("YYYY-MM-DD");
        newEndDate = today.subtract(1, "year").endOf("year").format("YYYY-MM-DD");
        break;
      default:
        newStartDate = "";
        newEndDate = "";
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCurrentPage(1);
    fetchNotificationsWithParams(1, itemsPerPage, searchQuery, newStartDate, newEndDate);
  };

  // Initialize default date filter on mount
  useEffect(() => {
    if (dateFilterOption !== "custom") {
      const today = dayjs();
      let newStartDate = "";
      let newEndDate = "";

      switch (dateFilterOption) {
        case "last7days":
          newStartDate = today.subtract(7, "day").format("YYYY-MM-DD");
          newEndDate = today.format("YYYY-MM-DD");
          break;
        case "last30days":
          newStartDate = today.subtract(30, "day").format("YYYY-MM-DD");
          newEndDate = today.format("YYYY-MM-DD");
          break;
        case "thisMonth":
          newStartDate = today.startOf("month").format("YYYY-MM-DD");
          newEndDate = today.format("YYYY-MM-DD");
          break;
        case "lastMonth":
          newStartDate = today.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
          newEndDate = today.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
          break;
        case "thisYear":
          newStartDate = today.startOf("year").format("YYYY-MM-DD");
          newEndDate = today.format("YYYY-MM-DD");
          break;
        case "lastYear":
          newStartDate = today.subtract(1, "year").startOf("year").format("YYYY-MM-DD");
          newEndDate = today.subtract(1, "year").endOf("year").format("YYYY-MM-DD");
          break;
        default:
          break;
      }

      if (newStartDate && newEndDate) {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle date filter changes
  useEffect(() => {
    if (dateFilterOption === "custom") {
      setCurrentPage(1);
      fetchNotificationsWithParams(1, itemsPerPage, searchQuery, startDate, endDate);
    }
  }, [startDate, endDate]);

  const clearFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setSelectedType("all");
    setDateFilterOption("last7days");
    setCurrentPage(1);
    fetchNotificationsWithParams(1, itemsPerPage, "", "", "");
  };

  const hasActiveFilters =
    searchQuery || startDate || endDate || selectedType !== "all";

  const handleNotificationClick = async (id, notificationId, type) => {
    try {
      let baseRoute = "";
      let navigationState = [];

      if (location?.pathname?.includes("staff-member")) {
        baseRoute = "staff-member/";
      } else if (location?.pathname?.includes("customers")) {
        baseRoute = "customers/";
      } else if (location?.pathname?.includes("donors")) {
        baseRoute = "donors/";
      } else {
        baseRoute = "";
      }

      let route = "";
      let tempRoute = "";

      switch (type) {
        case "quote":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-quotes-details"
              : location?.pathname?.includes("customers")
              ? "quotes-details"
              : "quotes-detail"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-quote"
              : location?.pathname?.includes("customers")
              ? "quotes"
              : "quotes"
          }`;

          break;
        case "contract":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-contract-details"
              : location?.pathname?.includes("customers")
              ? "contract-details"
              : "contract-details"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-contract"
              : location?.pathname?.includes("customers")
              ? "contract"
              : "contract"
          }`;
          break;
        case "customer":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "customer-details"
              : "customerdetails"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-customer"
              : "customer"
          }`;
          break;
        case "invoice":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-invoice-detail"
              : location?.pathname?.includes("customers")
              ? "invoice-payments"
              : "invoice-details"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-invoice"
              : location?.pathname?.includes("customers")
              ? "invoice"
              : "invoice"
          }`;
          break;
        case "Chnge":
        case "approve":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-quotes-details"
              : location?.pathname?.includes("customers")
              ? "quotes-details"
              : "quotes-detail"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-quote"
              : location?.pathname?.includes("customers")
              ? "quotes"
              : "quotes"
          }`;
          break;
        case "worker":
          route = "add-user";
          break;
        case "donor":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "donordetails"
              : location?.pathname?.includes("customers")
              ? "donordetails"
              : location?.pathname?.includes("donors")
              ? "profile"
              : "donordetails"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-donor"
              : location?.pathname?.includes("customers")
              ? "donor"
              : "donor"
          }`;
          break;
        case "productandservice":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "materials-labor"
              : "materials-labor"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "manageteam"
              : "manageteam"
          }`;
          break;
        case "account":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member") ? "account" : "account"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "manage-visits"
              : "manage-visits"
          }`;
          break;

        case "templates":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member") ? "" : "add-templates"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member") ? "" : "templates"
          }`;
          break;

        case "recurring-payment":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "customer-details"
              : "customerdetails"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-customer"
              : "customer"
          }`;
          break;

        case "recurring-charges":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "customer-details"
              : "customerdetails"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-customer"
              : "customer"
          }`;
          break;
        case "visit-add":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "customer-details"
              : "contractdetails"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-contract"
              : "contract"
          }`;
          break;
        case "ach-payment":
        case "payment":
        case "card-payment":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "invoice-detail"
              : "invoice-details"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-invoice"
              : "invoice"
          }`;
          break;

        case "VisitReminder":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-contract-details"
              : "contract-details"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-contract"
              : "contract"
          }`;
          break;

        case "billing-history":
          route = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "customer-details"
              : "customerdetails"
          }`;
          tempRoute = `${baseRoute}${
            location?.pathname?.includes("staff-member")
              ? "worker-customer"
              : "customer"
          }`;
          break;

        case "company":
          // For SuperAdmin, navigate to company management
          if (location?.pathname?.includes("superadmin")) {
            route = "company";
            tempRoute = "company";
          } else {
            route = "";
          }
          break;

        default:
          route = "";
      }

      navigationState = route.split("/");
      if (route) {
        // Handle SuperAdmin routes differently
        if (location?.pathname?.includes("superadmin")) {
          navigate(`/superadmin/${route}`, {
            state: { id: id, navigats: ["/index", ...navigationState] },
            replace: true,
          });
        } else {
          if (tempRoute) {
            navigate(`/${CompanyUrl}/${tempRoute}`, {
              state: { id: id, navigats: ["/index", ...navigationState] },
              replace: true,
            });
          }

          setTimeout(() => {
            //temporary navigate to home page
            navigate(`/${CompanyUrl}/${route}`, {
              state: { id: id, navigats: ["/index", ...navigationState] },
              replace: true,
            });
          }, 5);
        }
      }
      handleCloseNotifications();

      await Promise.all([
        AxiosInstance.put(`/v1/notification/${notificationId}`),
        fetchNotifications(),
      ]);
    } catch (error) {
      console.error("Error handling notification click:", error.message);
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
          <Box
            className="notification-feed-search"
            sx={{
              padding: "20px 24px",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#ffffff",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <DebouncedSearchField
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search notifications..."
              isDonationMode={isDonationMode}
            />

            {/* First Row: Filters and Per Page */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                marginBottom: 1.5,
              }}
            >
              <Button
                variant={showFilters ? "contained" : "outlined"}
                startIcon={<FilterIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFilters(!showFilters);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontWeight: 600,
                  fontSize: "14px",
                  borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                  color: showFilters 
                    ? "#fff" 
                    : (isDonationMode ? "#68b2cb" : "#0f2b46"),
                  backgroundColor: showFilters
                    ? (isDonationMode ? "#68b2cb" : "#0f2b46")
                    : "transparent",
                  boxShadow: showFilters 
                    ? "0 2px 4px rgba(0, 0, 0, 0.1)" 
                    : "none",
                  flex: 1,
                  "&:hover": {
                    borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                    backgroundColor: showFilters
                      ? (isDonationMode ? "#5a9db8" : "#052a52")
                      : (isDonationMode
                          ? "rgba(104, 178, 203, 0.1)"
                          : "rgba(6, 49, 100, 0.1)"),
                    transform: "translateY(-1px)",
                    boxShadow: "var(--shadow-md)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Filters
              </Button>
              
              <FormControl
                size="small"
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                      borderWidth: "1.5px",
                    },
                    "&:hover fieldset": {
                      borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                      borderWidth: "2px",
                    },
                  },
                }}
              >
                <InputLabel
                  sx={{
                    color: isDonationMode ? "#68b2cb" : "#0f2b46",
                    fontWeight: 500,
                    "&.Mui-focused": {
                      color: isDonationMode ? "#68b2cb" : "#0f2b46",
                    },
                  }}
                >
                  Per Page
                </InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Per Page"
                  onChange={handleItemsPerPageChange}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    color: isDonationMode ? "#68b2cb" : "#0f2b46",
                    fontWeight: 500,
                    "& .MuiSvgIcon-root": {
                      color: isDonationMode ? "#68b2cb" : "#0f2b46",
                    },
                  }}
                >
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                  <MenuItem value={300}>300</MenuItem>
                  <MenuItem value={400}>400</MenuItem>
                </Select>
              </FormControl>

            </Box>

            {/* Second Row: Total Count */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 0",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  fontSize: "13px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <span style={{ color: isDonationMode ? "#68b2cb" : "#0f2b46", fontWeight: 600 }}>
                  {totalCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} - {Math.min(currentPage * itemsPerPage, totalCount)}
                </span>
                <span>of</span>
                <span style={{ color: isDonationMode ? "#68b2cb" : "#0f2b46", fontWeight: 600 }}>
                  {totalAllNotifications > 0 ? totalAllNotifications : totalCount}
                </span>
                <span>Notifications</span>
                {totalAllNotifications > 0 && totalAllNotifications !== totalCount && (
                  <Box
                    component="span"
                    sx={{
                      color: "#9ca3af",
                      fontSize: "11px",
                      backgroundColor: "#f3f4f6",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      marginLeft: 0.5,
                    }}
                  >
                    {totalCount} filtered
                  </Box>
                )}
              </Typography>
            </Box>

            {showFilters && (
              <Box
                className="notification-feed-filters"
                style={{ width: "100%" }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  marginTop: 2,
                  padding: 2.5,
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  border: `2px solid ${isDonationMode ? "#68b2cb" : "#0f2b46"}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "stretch",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    width: "100%",
                    padding: 2,
                    borderRadius: "10px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: isDonationMode ? "#68b2cb" : "#0f2b46",
                      marginBottom: 0.5,
                      fontSize: "15px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DateRangeIcon sx={{ fontSize: "18px" }} />
                    Date Range
                  </Typography>

                  <FormControl
                    size="small"
                    fullWidth
                    sx={{
                      marginBottom: 2,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                          borderWidth: "1.5px",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                          borderWidth: "2px",
                        },
                      },
                    }}
                  >
                    <InputLabel
                      sx={{
                        color: isDonationMode ? "#68b2cb" : "#0f2b46",
                        fontWeight: 500,
                        "&.Mui-focused": {
                          color: isDonationMode ? "#68b2cb" : "#0f2b46",
                        },
                      }}
                    >
                      Date Filter
                    </InputLabel>
                    <Select
                      value={dateFilterOption}
                      label="Date Filter"
                      onChange={handleDateFilterOptionChange}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{
                        color: isDonationMode ? "#68b2cb" : "#0f2b46",
                        fontWeight: 500,
                        "& .MuiSvgIcon-root": {
                          color: isDonationMode ? "#68b2cb" : "#0f2b46",
                        },
                      }}
                    >
                      <MenuItem value="last7days">Last 7 Days</MenuItem>
                      <MenuItem value="last30days">Last 30 Days</MenuItem>
                      <MenuItem value="thisMonth">This Month</MenuItem>
                      <MenuItem value="lastMonth">Last Month</MenuItem>
                      <MenuItem value="thisYear">This Year</MenuItem>
                      <MenuItem value="lastYear">Last Year</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>

                  {dateFilterOption === "custom" && (
                    <>
                      <DatePicker
                        name="startDate"
                        label="Start Date"
                        value={getDatePickerValue(startDate)}
                        format={dateFormat}
                        placeholder={dateFormat}
                        inputRef={startDateInputRef}
                        open={startDatePickerOpen}
                        onOpen={() => setStartDatePickerOpen(true)}
                        onClose={() => setStartDatePickerOpen(false)}
                        onChange={(e) => {
                          // Handle calendar selections immediately
                          const newStartDate = formatDateForAPI(e);
                          if (newStartDate) {
                            setStartDate(newStartDate);
                            if (
                              endDate &&
                              newStartDate &&
                              new Date(newStartDate) > new Date(endDate)
                            ) {
                              setEndDate("");
                            }
                          }
                        }}
                        onAccept={(acceptedValue) => {
                          // Handle manual input completion - only fires when date is fully entered and valid
                          if (acceptedValue && dayjs.isDayjs(acceptedValue) && acceptedValue.isValid()) {
                            const newStartDate = acceptedValue.format('YYYY-MM-DD');
                            setStartDate(newStartDate);
                            if (
                              endDate &&
                              newStartDate &&
                              new Date(newStartDate) > new Date(endDate)
                            ) {
                              setEndDate("");
                            }
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "white",
                                borderRadius: "8px",
                                "& fieldset": {
                                  borderColor: "#e5e7eb",
                                  borderWidth: "1.5px",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                                  borderWidth: "2px",
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontWeight: 500,
                                color: "#374151",
                                fontSize: "14px",
                                padding: "10px 14px",
                              },
                              "& .MuiInputLabel-root": {
                                color: isDonationMode ? "#68b2cb" : "#0f2b46",
                                fontWeight: 600,
                                fontSize: "13px",
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: "11px",
                                color: isDonationMode ? "rgba(104, 178, 203, 0.7)" : "rgba(6, 49, 100, 0.7)",
                                marginTop: "4px",
                                marginLeft: "0px",
                              },
                            },
                          },
                        }}
                      />

                      <DatePicker
                        name="endDate"
                        label="End Date"
                        value={getDatePickerValue(endDate)}
                        format={dateFormat}
                        placeholder={dateFormat}
                        inputRef={endDateInputRef}
                        minDate={startDate ? getDatePickerValue(startDate) : null}
                        open={endDatePickerOpen}
                        onOpen={() => setEndDatePickerOpen(true)}
                        onClose={() => setEndDatePickerOpen(false)}
                        onChange={(e) => {
                          // Handle calendar selections immediately
                          const newEndDate = formatDateForAPI(e);
                          if (newEndDate) {
                            if (
                              !startDate ||
                              !newEndDate ||
                              new Date(newEndDate) >= new Date(startDate)
                            ) {
                              setEndDate(newEndDate);
                            }
                          }
                        }}
                        onAccept={(acceptedValue) => {
                          // Handle manual input completion - only fires when date is fully entered and valid
                          if (acceptedValue && dayjs.isDayjs(acceptedValue) && acceptedValue.isValid()) {
                            const newEndDate = acceptedValue.format('YYYY-MM-DD');
                            if (
                              !startDate ||
                              !newEndDate ||
                              new Date(newEndDate) >= new Date(startDate)
                            ) {
                              setEndDate(newEndDate);
                            }
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "white",
                                borderRadius: "8px",
                                "& fieldset": {
                                  borderColor: "#e5e7eb",
                                  borderWidth: "1.5px",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: isDonationMode ? "#68b2cb" : "#0f2b46",
                                  borderWidth: "2px",
                                },
                              },
                              "& .MuiInputBase-input": {
                                fontWeight: 500,
                                color: "#374151",
                                fontSize: "14px",
                                padding: "10px 14px",
                              },
                              "& .MuiInputLabel-root": {
                                color: isDonationMode ? "#68b2cb" : "#0f2b46",
                                fontWeight: 600,
                                fontSize: "13px",
                              },
                              "& .MuiFormHelperText-root": {
                                fontSize: "11px",
                                color: isDonationMode ? "rgba(104, 178, 203, 0.7)" : "rgba(6, 49, 100, 0.7)",
                                marginTop: "4px",
                                marginLeft: "0px",
                              },
                            },
                          },
                        }}
                      />
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {loader ? (
            <Grid
              className="d-flex flex-direction-row justify-content-center align-items-center"
              style={{
                height: "100%",
                minHeight: "400px",
                width: "100%",
                padding: "40px 24px",
              }}
            >
              <Grid className="d-flex flex-direction-column justify-content-center align-items-center">
                <LoaderComponent
                  loader={loader}
                  height="60"
                  width="60"
                  isDonationMode={isDonationMode}
                />
              </Grid>
            </Grid>
          ) : (
            <>
          <Grid className="notificationsContainer quotes-notification">
            {paginatedNotifications?.length > 0 ? (
              paginatedNotifications?.map((notification) => {
                return (
                  <NotificationContainer
                    key={notification?.NotificationId}
                    handleNotificationClick={handleNotificationClick}
                    notification={notification}
                  />
                );
              })
            ) : (
              <Grid
                className="mt-4 text-center"
                style={{
                  color: isDonationMode ? "#68b2cb" : "#0f2b46",
                  padding: "40px 24px",
                }}
              >
                {hasActiveFilters ? (
                  <>
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      No notifications match your filters.
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      Try adjusting your search criteria or clearing the
                      filters.
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h6">
                    No notifications available.
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>

          {/* Pagination Controls */}
          {totalCount > 0 && totalPages > 1 && (
            <Box
              className="notification-feed-pagination"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                padding: "20px 24px",
                borderTop: "2px solid #e5e7eb",
                backgroundColor: "#ffffff",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              {/* Page Info and Navigation */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  {/* <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                  >
                    Page
                  </Typography>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      backgroundColor: isDonationMode ? "rgba(104, 178, 203, 0.1)" : "rgba(6, 49, 100, 0.1)",
                      padding: "4px 12px",
                      borderRadius: "8px",
                      border: `1.5px solid ${isDonationMode ? "#68b2cb" : "#0f2b46"}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDonationMode ? "#68b2cb" : "#0f2b46",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {currentPage}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#9ca3af",
                        fontSize: "13px",
                      }}
                    >
                      / {totalPages}
                    </Typography>
                  </Box> */}
                </Box> 

                {/* Custom Pagination Controls */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentPage > 1) handlePageChange(e, 1);
                    }}
                    disabled={currentPage === 1}
                    sx={{
                      minWidth: "28px",
                      height: "28px",
                      padding: 0,
                      borderRadius: "8px",
                      border: `1.5px solid ${isDonationMode ? "#68b2cb" : "#0f2b46"}`,
                      color: isDonationMode ? "#68b2cb" : "#0f2b46",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: isDonationMode
                          ? "rgba(104, 178, 203, 0.1)"
                          : "rgba(6, 49, 100, 0.1)",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#e5e7eb",
                        color: "#d1d5db",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    <FirstPageIcon fontSize="small" />
                  </Button>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentPage > 1) handlePageChange(e, currentPage - 1);
                    }}
                    disabled={currentPage === 1}
                    sx={{
                      minWidth: "28px",
                      height: "28px",
                      padding: 0,
                      borderRadius: "8px",
                      border: `1.5px solid ${isDonationMode ? "#68b2cb" : "#0f2b46"}`,
                      color: isDonationMode ? "#68b2cb" : "#0f2b46",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: isDonationMode
                          ? "rgba(104, 178, 203, 0.1)"
                          : "rgba(6, 49, 100, 0.1)",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#e5e7eb",
                        color: "#d1d5db",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </Button>

                  {/* Page Numbers */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePageChange(e, pageNum);
                          }}
                          sx={{
                            minWidth: "28px",
                            height: "28px",
                            padding: 0,
                            borderRadius: "8px",
                            border: `1.5px solid ${
                              currentPage === pageNum
                                ? "transparent"
                                : isDonationMode
                                ? "#68b2cb"
                                : "#0f2b46"
                            }`,
                            color:
                              currentPage === pageNum
                                ? "#fff"
                                : isDonationMode
                                ? "#68b2cb"
                                : "#0f2b46",
                            backgroundColor:
                              currentPage === pageNum
                                ? isDonationMode
                                  ? "#68b2cb"
                                  : "#0f2b46"
                                : "transparent",
                            fontWeight: currentPage === pageNum ? 700 : 500,
                            fontSize: "14px",
                            boxShadow:
                              currentPage === pageNum
                                ? "0 2px 4px rgba(0, 0, 0, 0.1)"
                                : "none",
                            "&:hover": {
                              backgroundColor:
                                currentPage === pageNum
                                  ? isDonationMode
                                    ? "#5a9db8"
                                    : "#052a52"
                                  : isDonationMode
                                  ? "rgba(104, 178, 203, 0.1)"
                                  : "rgba(6, 49, 100, 0.1)",
                              transform: "translateY(-1px)",
                              boxShadow: "var(--shadow-md)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </Box>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentPage < totalPages) handlePageChange(e, currentPage + 1);
                    }}
                    disabled={currentPage === totalPages}
                    sx={{
                      minWidth: "28px",
                      height: "28px",
                      padding: 0,
                      borderRadius: "8px",
                      border: `1.5px solid ${isDonationMode ? "#68b2cb" : "#0f2b46"}`,
                      color: isDonationMode ? "#68b2cb" : "#0f2b46",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: isDonationMode
                          ? "rgba(104, 178, 203, 0.1)"
                          : "rgba(6, 49, 100, 0.1)",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#e5e7eb",
                        color: "#d1d5db",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </Button>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentPage < totalPages) handlePageChange(e, totalPages);
                    }}
                    disabled={currentPage === totalPages}
                    sx={{
                        minWidth: "28px",
                      height: "28px",
                      padding: 0,
                      borderRadius: "8px",
                      border: `1.5px solid ${isDonationMode ? "#68b2cb" : "#0f2b46"}`,
                      color: isDonationMode ? "#68b2cb" : "#0f2b46",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: isDonationMode
                          ? "rgba(104, 178, 203, 0.1)"
                          : "rgba(6, 49, 100, 0.1)",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#e5e7eb",
                        color: "#d1d5db",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    <LastPageIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
            </>
          )}
        </Box>
    </>
  );
};

export default NotificationsPage;
